import { all, call, delay, put, select, spawn, take, takeEvery } from 'redux-saga/effects';

import dynaForms from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { validateEmailAlreadyExist } from '@elab/core/utils/dynaForms/helpers';
import { actions as authActions } from '@elab/core/store/auth';
import {
    actions as cartActions,
    selectors as cartSelectors,
    types as cartTypes,
} from '@elab/core/store/cart';
import cartApi from '@elab/core/store/cart/api';
import { api as deliveryAddressApi } from '@elab/components/connected/forms/DeliveryAddressForm/redux';
import { actions as customerAddressesAction } from '@elab/components/connected/customerAddresses/redux';
import { selectors as userSelectors, types as userTypes } from '@elab/core/store/user';
import { types as socialTypes } from '@elab/core/store/social';
import { actions as uiActions } from '@elab/core/store/ui';
import { selectors as configSelectors } from '@elab/core/store/config';
import { setAnalyticsCookies } from '@elab/core/store/address/saga';
import { actions as globalActions } from '@elab/core/store/global';
import analytics from '@elab/core/utils/analytics';
import { actions as analyticsActions } from '@elab/core/store/analytics';
import isEmpty from '@elab/core/utils/isEmpty';
import {
    copySecondAddressFields,
    getConfirmationFromModal,
    initAddressSuggestionWatchers,
} from '@elab/core/utils/address/sagaHelpers';
import { setPrefillData } from '@elab/core/utils/personalInfo';
import { backSocialActions, constants, loyaltyStatuses } from '@elab/core/utils/constants';
import { isAddressesEqual } from '@elab/core/utils/address/helpers';
import verifyPhoneFormSelectors from '@elab/components/connected/forms/PhoneVerificationForm/redux/selectors';

import getRegTypes from '../../RegistrationForm/redux/types';
import getRegSelectors from '../../RegistrationForm/redux/selectors';
import getRegActions from '../../RegistrationForm/redux/actions';
import {
    afterStepValidation,
    beforeStepIncrement,
    beforeStepValidation,
    customDtoBuilder,
    doRegister,
    doSubmitWithoutPhoneVerification,
    handleCardNumberInitialization,
    populateNestedForm,
    radioButtonChanges,
    setDataField,
    setDataFieldValue,
    setStep,
    stepBack,
    stepSubmit,
    verifyPhone,
} from '../../RegistrationForm/redux/saga';

import verifyPhoneFormActions from '../../PhoneVerificationForm/redux/actions';
import storeNames from '../storeNames';

import api from './api';

import { createActions, getSelectors, getTypes } from '.';

const checkoutValues = {
    login: 'checkout-login',
    registration: 'checkout-registration',
    email: 'checkout-email',
    registrationOption: 'checkout-registration-option',
    guest: 'checkout-guest',
    checkoutCrmUnavailable: 'checkout-crm-unavailable',
};

export function* watchSetInitialData(storeName) {
    const actions = createActions(storeName);
    const types = getTypes(storeName);

    yield take(types.SET_INITIAL_DATA);
    let cartData = yield select(cartSelectors.getData);
    if (isEmpty(cartData)) {
        yield take(cartTypes.SET_CART_DATA);
        cartData = yield select(cartSelectors.getData);
    }

    if (storeName === storeNames.CHECKOUT_GUEST_TO_LOGIN_FORM) {
        yield put(actions.setAuthOption({ option: checkoutValues.email }));
        yield spawn(analytics.handleGTLEmailStep);
    }

    const isGuestCheckoutAllowed = yield select(cartSelectors.isGuestCheckoutAllowed);
    const sso = new URL(window.location.href).searchParams.get('sso');

    if (!isGuestCheckoutAllowed || sso) {
        yield put(actions.hideGuestCheckout());
    }

    const paymentAddress = yield select(cartSelectors.getPaymentAddress);
    if (isGuestCheckoutAllowed && cartData && !isEmpty(paymentAddress)) {
        yield put(actions.setDataFieldValue('checkoutOptions', 'checkout-guest'));
    }

    yield put(actions.setFormIsLoaded());
}

export function updateTab({ actions }) {
    return function*({ payload: { checkout, action } = {} }) {
        if (checkout) {
            const checkoutValuesByActions = {
                [backSocialActions.register]: checkoutValues.registration,
                [backSocialActions.login]: checkoutValues.login,
                [backSocialActions.connect]: checkoutValues.login,
            };
            const value = checkoutValuesByActions[action];
            if (value) {
                yield put(actions.setDataFieldValue('checkoutOptions', value, true));
            }
        }
    };
}

export function* watchFormInitialize({ types, actions }, storeName) {
    yield take(types.AFTER_INITIALIZE);
    let user = yield select(userSelectors.getProfile);
    const notPopulatePersonalDataForGuest = yield select(
        configSelectors.notPopulatePersonalDataForGuest,
    );
    const notPopulatePersonalDataForCustomer = yield select(
        configSelectors.notPopulatePersonalDataForCustomer,
    );

    if (isEmpty(user)) {
        yield take(userTypes.SET_PROFILE);
        user = yield select(userSelectors.getProfile);
    }

    if (
        (!user.anonymous && notPopulatePersonalDataForCustomer) ||
        (user.anonymous && notPopulatePersonalDataForGuest)
    )
        return {};

    let address = yield select(cartSelectors.getAnonymousAddress);

    const billingAddress = yield call(isBillingAddress, storeName);

    if (
        storeName === storeNames.CHANGE_BILLING_ADDRESS_FORM ||
        (storeName === storeNames.CHECKOUT_GUEST && billingAddress)
    ) {
        address = yield select(cartSelectors.getPaymentAddress);
    }

    const isDeliveryModePickup = yield select(cartSelectors.isDeliveryModePickup);

    if (
        storeName === storeNames.DELIVERY_ADDRESS_FOR_LOGGED_IN_USER_FORM ||
        (storeName === storeNames.CHECKOUT_GUEST && !billingAddress && !isDeliveryModePickup)
    ) {
        yield put(cartActions.loadCart());
        yield take(cartTypes.SET_CART_DATA);

        address = yield select(cartSelectors.getDeliveryAddress);
    }

    if (storeName === storeNames.DELIVERY_ADDRESS_FOR_LOGGED_IN_USER_FORM) {
        const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);

        if (isEmpty(deliveryAddress)) {
            address = yield select(cartSelectors.getPaymentAddress);
        }
    }
    yield call(populatePersonalData, actions, address);
    yield call(setDefaultCountryWhenSelectedIsUnavailable, storeName, actions, address.country);

    const addAddress = yield call(addAddressField, storeName);

    if (storeName !== storeNames.CHECKOUT_GUEST || !addAddress) {
        return;
    }
    const maybeBillingAddress = yield select(cartSelectors.getPaymentAddress);
    const isDeliveryModeHome = yield select(cartSelectors.isDeliveryModeHome);
    const compareAddressConfig = yield select(configSelectors.getCompareAddress);
    if (!maybeBillingAddress.id || maybeBillingAddress.id === address.id || !isDeliveryModeHome) {
        return;
    }
    yield put(actions.addFieldItem({ name: constants.FIELD_NAMES.addAddress }));
    const copyEffect = copySecondAddressFields(actions, maybeBillingAddress);
    const billingCopyEffects = Object.keys(maybeBillingAddress).map(copyEffect);
    yield all(billingCopyEffects);

    const isDeliveryAddressEqualBillingAddress = yield call(
        isAddressesEqual,
        maybeBillingAddress,
        address,
        compareAddressConfig.comparableProperties,
        compareAddressConfig.comparablePropertiesForCountry,
    );

    if (isDeliveryAddressEqualBillingAddress) {
        yield put(actions.setDataFieldValue('addAddress', null));
    }
}

export function* updateRegistrationEmailFieldValue({ actions }, email = '') {
    yield put(actions.setDataFieldValue('uid', email, true));
    yield put(actions.setDataFieldValue('uidConfirm', email, true));
    yield put(
        actions.setDataFieldReadonlyStatus({
            name: 'uid',
            status: true,
        }),
    );
    yield put(
        actions.setDataFieldReadonlyStatus({
            name: 'uidConfirm',
            status: true,
        }),
    );
}

export function* updateGuestEmailFieldValue({ actions }, email = '') {
    yield put(actions.setDataFieldValue('email', email, true));
}

export function* clearRegistrationPasswordFieldsValue({ actions }) {
    yield put(actions.setDataFieldValue('password', '', true));
    yield put(actions.setDataFieldValue('passwordConfirm', '', true));
}

export function* updateBeautyMemberSubform({ actions }) {
    yield put(actions.addFieldItem({ name: 'physicalRegistration' }));
    yield put(
        actions.setDataFieldDisabledStatus({
            name: 'physicalRegistration',
            status: true,
        }),
    );
}

export function* clearBeautyMemberSubform({ actions }) {
    yield put(
        actions.setDataFieldDisabledStatus({
            name: 'physicalRegistration',
            status: false,
        }),
    );
    yield put(actions.setDataFieldValue('physicalRegistration.value.0.cardNumber', '', false));
    yield take(getRegTypes(checkoutValues.registration).DATA_FIELD_VALIDATION_DONE);
    yield put(actions.setDataFieldValue('physicalRegistration.value.0.activationCode', '', false));
    yield take(getRegTypes(checkoutValues.registration).DATA_FIELD_VALIDATION_DONE);
    yield put(actions.setDataFieldValue('physicalRegistration', null, true));
}

const afterCheckoutRegistrationInitialize = ctx =>
    function*() {
        const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
        if (isNewLoyaltyFlow) {
            // Need to place this check in order to remove readonly attribute from first step
            yield put(ctx.actions.phoneDobReceived({ isLoyalty: false, forceReadOnly: false }));
        }
        yield call(populateNestedForm, ctx);
    };

export function* setDefaultCountryWhenSelectedIsUnavailable(storeName, actions, { isocode } = {}) {
    if (storeName === storeNames.DELIVERY_ADDRESS_FOR_LOGGED_IN_USER_FORM) {
        let countryField = yield call(getCountryField, storeName);
        if (!(countryField.values || []).some(country => country.name === isocode)) {
            yield put(actions.setDataFieldValue('country', countryField.defaultValue));
        }
    }
}

export function* getCountryField(storeName) {
    return yield select(state => getSelectors(storeName).getDataField(state, 'country')) || {};
}

export function* isBillingAddress(storeName) {
    return yield select(state =>
        getSelectors(storeName).getDataFieldValue(state, constants.FIELD_NAMES.billingAddress),
    );
}

export function* addAddressField(storeName) {
    return yield select(state =>
        getSelectors(storeName).getDataField(state, constants.FIELD_NAMES.addAddress),
    );
}

export const setSocialInfo = ctx =>
    function*({ payload: { action, data } }) {
        if (action === backSocialActions.register && data) {
            yield put(ctx.actions.initialize());
            yield delay(500);
            yield call(resetCheckoutData, ctx.actions, ctx.selectors);
            yield call(setPrefillData, ctx, data);
        }
    };

export function* resetCheckoutData(actions, selectors) {
    const metadata = yield select(selectors.getMetadata);
    const stepMetaDataFields = metadata[1].fields;
    const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);
    const cardNumberField = `${loyaltyPrefix}cardNumber`;

    const parentFormType = { parentFormId: 'checkout-registration' };
    yield put(verifyPhoneFormActions.initialize());
    yield put(verifyPhoneFormActions.resetForm(parentFormType));
    yield put(verifyPhoneFormActions.phoneVerificationClose());
    yield put(actions.showPincode(false));
    yield put(actions.setStep(1));
    yield put(actions.setLoyaltyFlag(false));
    yield put(actions.disableStep1Button({ disableStep1Button: false }));
    yield put(actions.setLoading(false));
    yield put(actions.setStepValidationLoading(false));

    for (const field in stepMetaDataFields) {
        if (
            stepMetaDataFields[field].type !== 'HIDDEN' &&
            stepMetaDataFields[field].name !== 'loyaltyOptionsForRegistrationForm'
        ) {
            if (stepMetaDataFields[field].name === 'mobileNumber') {
                yield put(actions.setDataFieldValue(field, '(5', true));
                yield put(
                    actions.setDataFieldReadonlyStatus({
                        name: 'mobileNumber',
                        status: false,
                    }),
                );
            } else {
                yield put(actions.setDataFieldValue(field, null, true));
                yield put(actions.setDataFieldValue(cardNumberField, null, true));
                yield put(verifyPhoneFormActions.setDataFieldValue('code', null, true));
                yield put(
                    actions.setDataFieldReadonlyStatus({
                        name: field,
                        status: false,
                    }),
                );
            }
        }
    }
    yield put(
        verifyPhoneFormActions.setDataFieldReadonlyStatus({
            name: 'code',
            status: false,
        }),
    );
}

export function* populatePersonalData(actions, address) {
    for (const fieldName in address) {
        if (address[fieldName]) {
            yield put(
                actions.setDataFieldValue(
                    fieldName,
                    fieldName === 'country' ? address[fieldName].isocode : address[fieldName],
                    true,
                    true,
                ),
            );
        }
    }
}

export function* guestSubmit(
    rawDto,
    ctx,
    submitUrl,
    dialogInfo,
    formType,
    step,
    continueAsGuest,
    globalAuthErrorMessage,
) {
    let dto = { ...rawDto };
    if ((formType === 'guestToLoginForm' && step < 2) || (continueAsGuest && step === 2)) {
        dto = { email: rawDto.uid };
    }

    const successRedirectUrl = yield select(ctx.selectors.getSuccessRedirectLink);
    let cartId = yield select(cartSelectors.getGuid);
    const regularCartId = yield select(cartSelectors.getRegularCartId);
    if (regularCartId && regularCartId !== cartId) {
        cartId = regularCartId;
    }
    const addAddressesToGuestCart = yield select(configSelectors.addAddressesToGuestCart);
    if (addAddressesToGuestCart) {
        const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);
        const billingAddress = yield select(cartSelectors.getPaymentAddress);
        const { email } = dto;
        const isDeliveryModePickup = yield select(cartSelectors.isDeliveryModePickup);
        if (deliveryAddress.id && !isDeliveryModePickup) {
            dto.deliveryAddress = { ...deliveryAddress, email };
        }
        if (billingAddress.id) {
            dto.billingAddress = { ...billingAddress, email };
        }
    }

    if (!continueAsGuest && formType === 'guestToLoginForm' && step === 2) {
        yield put(
            authActions.doLogin({
                successRedirectLink: successRedirectUrl,
                username: dto.username,
                password: dto.password,
                rememberMe: dto.rememberMe,
                formType: formType,
                globalAuthErrorMessage: globalAuthErrorMessage,
            }),
        );
        const customerLoyaltyStatus = yield select(
            getSelectors(storeNames.CHECKOUT_GUEST_TO_LOGIN_FORM).getCustomerLoyaltyStatus,
        );

        yield spawn(
            analytics.handleCheckoutAsGuest,
            storeNames.CHECKOUT_GUEST,
            customerLoyaltyStatus ? customerLoyaltyStatus === loyaltyStatuses.registered : null,
        );

        yield put(cartActions.loadCart());
        yield take(cartTypes.SET_CART_DATA);
        yield put(analyticsActions.setDeliveryOptionCompleted());
        yield call(setAnalyticsCookies);
    } else {
        yield call(cartApi.checkoutAsGuest, dto, cartId);
        const customerLoyaltyStatus = yield select(
            getSelectors(storeNames.CHECKOUT_GUEST_TO_LOGIN_FORM).getCustomerLoyaltyStatus,
        );

        yield spawn(
            analytics.handleCheckoutAsGuest,
            storeNames.CHECKOUT_GUEST,
            customerLoyaltyStatus ? customerLoyaltyStatus === loyaltyStatuses.registered : null,
        );

        yield put(cartActions.loadCart());
        yield take(cartTypes.SET_CART_DATA);
        yield put(analyticsActions.setDeliveryOptionCompleted());
        yield call(setAnalyticsCookies);
        yield put(globalActions.redirect(successRedirectUrl));
    }
}

export function* billingSubmit(dto, ctx) {
    const successRedirectUrl = yield select(ctx.selectors.getSuccessRedirectLink);
    const cartId = yield select(cartSelectors.getCartId);
    const userId = yield select(userSelectors.getUserId);
    const paymentAddress = yield select(cartSelectors.getPaymentAddress);

    if (paymentAddress.id && !dto.id) {
        dto.id = paymentAddress.id;
    }

    yield call(api.setBillingAddress, userId, cartId, dto);

    yield put(cartActions.loadCart());
    yield take(cartTypes.SET_CART_DATA);
    if (!userId) {
        yield put(globalActions.redirect(successRedirectUrl));
    } else {
        yield put(customerAddressesAction.loadBillingAddresses());
        yield put(uiActions.scrollToTopOfPage());
    }
}

export function* deliverySubmit(dto, ctx) {
    const successRedirectUrl = yield select(ctx.selectors.getSuccessRedirectLink);
    const cartId = yield select(cartSelectors.getCartId);
    const userId = yield select(userSelectors.getUserId);

    yield call(deliveryAddressApi.setDeliveryAddress, userId, cartId, dto);

    yield put(cartActions.loadCart());
    yield take(cartTypes.SET_CART_DATA);

    if (!userId) {
        yield put(globalActions.redirect(successRedirectUrl));
    } else {
        yield put(customerAddressesAction.loadAddresses());
        yield put(uiActions.scrollToTopOfPage());
    }
}

export function* registeredSubmit(dto, ctx, submitUrl, dialogInfo = {}) {
    const shouldVerifyPhone = !isEmpty(yield select(verifyPhoneFormSelectors.getData));
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    if (shouldVerifyPhone && !isNewLoyaltyFlow) {
        yield spawn(verifyPhone, dto, ctx.selectors, ctx.actions, dialogInfo);
    } else {
        const successRedirectUrl = yield select(ctx.selectors.getSuccessRedirectLink);
        yield call(doRegister, dto, successRedirectUrl, ctx, dialogInfo, true);
        const customerLoyaltyStatus = yield select(
            getSelectors(storeNames.CHECKOUT_GUEST_TO_LOGIN_FORM).getCustomerLoyaltyStatus,
        );

        yield spawn(
            analytics.handleCheckoutRegistration,
            storeNames.CHECKOUT_REGISTRATION,
            customerLoyaltyStatus ? customerLoyaltyStatus === loyaltyStatuses.registered : null,
        );
    }
}

export function* emailSubmit(dto = {}, ctx) {
    const { actions, selectors } = ctx;
    const submitUrl = yield select(selectors.getSubmitUrl);
    const email = dto.email && dto.email.value;
    const status = yield call(dynaFormsApi.doSubmit, submitUrl, { email });
    const statusValue = status.value;
    yield put(actions.setCustomerLoyaltyStatus({ status: statusValue }));

    if (statusValue === loyaltyStatuses.registered) {
        yield put(actions.setAuthOption({ option: checkoutValues.login }));
        yield spawn(analytics.handleGTLCheckoutOption, true);
        yield call(
            updateGuestEmailFieldValue,
            { actions: getRegActions(checkoutValues.guest) },
            email,
        );
    } else if (statusValue === loyaltyStatuses.unregisteredNonLoyaltyMember) {
        yield put(actions.setAuthOption({ option: checkoutValues.registrationOption }));
        yield call(
            updateRegistrationEmailFieldValue,
            { actions: getRegActions(checkoutValues.registration) },
            email,
        );
        yield call(
            updateGuestEmailFieldValue,
            { actions: getRegActions(checkoutValues.guest) },
            email,
        );
        yield spawn(analytics.handleGTLCheckoutOption, false);
    } else if (statusValue === loyaltyStatuses.unregisteredLoyaltyMember) {
        yield put(actions.setAuthOption({ option: checkoutValues.registration }));
        yield call(
            updateRegistrationEmailFieldValue,
            { actions: getRegActions(checkoutValues.registration) },
            email,
        );
        yield call(
            updateGuestEmailFieldValue,
            { actions: getRegActions(checkoutValues.guest) },
            email,
        );
        yield call(updateBeautyMemberSubform, {
            actions: getRegActions(checkoutValues.registration),
        });
        yield spawn(analytics.handleGTLCheckoutOption, false);
    } else if (statusValue === loyaltyStatuses.unregisteredCrmUnavailable) {
        yield put(actions.setAuthOption({ option: checkoutValues.checkoutCrmUnavailable }));
        yield spawn(analytics.handleGTLError);
    }
}

const checkoutPreviousStep = ctx =>
    function*() {
        const { actions, selectors } = ctx;
        const authOption = (yield select(selectors.getStore)).authOption;
        const customerLoyaltyStatus = yield select(
            getSelectors(storeNames.CHECKOUT_GUEST_TO_LOGIN_FORM).getCustomerLoyaltyStatus,
        );

        if (authOption === checkoutValues.registration) {
            const registrationStep = yield select(
                getSelectors(checkoutValues.registration).getStep,
            );
            if (registrationStep !== 1) {
                yield put(getRegActions(checkoutValues.registration).setStep(1));
            } else if (customerLoyaltyStatus === loyaltyStatuses.unregisteredNonLoyaltyMember) {
                yield put(actions.setAuthOption({ option: checkoutValues.registrationOption }));
                yield call(clearBeautyMemberSubform, {
                    actions: getRegActions(checkoutValues.registration),
                });
            } else if (customerLoyaltyStatus === loyaltyStatuses.unregisteredLoyaltyMember) {
                yield put(actions.setAuthOption({ option: checkoutValues.email }));
                yield call(clearBeautyMemberSubform, {
                    actions: getRegActions(checkoutValues.registration),
                });
            }

            yield call(clearRegistrationPasswordFieldsValue, {
                actions: getRegActions(checkoutValues.registration),
            });
        }
    };

export const guestSaga = (storeName, formId) => {
    // checkout-guest saga uses the registration reducers, so we need to combine the redux artifacts
    const reduxArtifacts = {
        types: {
            ...getRegTypes(storeName),
            ...getTypes(storeName),
        },
        selectors: {
            ...getRegSelectors(storeName),
            ...getSelectors(storeName),
        },
        actions: {
            ...getRegActions(storeName),
            ...createActions(storeName),
        },
    };

    return [
        ...dynaForms.createSagaFor(
            reduxArtifacts,
            getFormMetadataFactory(formId),
            guestSubmit,
            getConfirmationFromModal,
            true,
        ),
        watchFormInitialize(reduxArtifacts, storeName),
        initAddressSuggestionWatchers(reduxArtifacts),
    ];
};

export const optionsSaga = (storeName, formId) => {
    const reduxArtifacts = {
        actions: getRegActions(storeName),
        selectors: getRegSelectors(storeName),
        types: getRegTypes(storeName),
    };

    return [
        ...dynaForms.createSagaFor(reduxArtifacts, dynaFormsApi.getFormMetadata(formId)),
        takeEvery(socialTypes.SET_SOCIAL_INFORMATION, updateTab(reduxArtifacts)),
        watchSetInitialData(storeName),
    ];
};

export const checkoutGuestToLoginSaga = (storeName, formId) => {
    const reduxArtifacts = {
        types: getTypes(storeName),
        selectors: getSelectors(storeName),
        actions: createActions(storeName),
    };

    return [
        ...dynaForms.createSagaFor(
            reduxArtifacts,
            dynaFormsApi.getFormMetadata(formId),
            emailSubmit,
        ),
        watchSetInitialData(storeName),
        takeEvery(reduxArtifacts.types.PREVIOUS_STEP, checkoutPreviousStep(reduxArtifacts)),
    ];
};

const getFormMetadataFactory = formId =>
    function*() {
        const cartData = yield select(cartSelectors.getData);

        if (isEmpty(cartData)) {
            yield take(cartTypes.SET_CART_DATA);
        }

        const cartContainsOtcProducts = yield select(cartSelectors.containsOtcProducts);
        const uri = dynaFormsApi.getFormMetadata(formId);
        return yield call(api.loadMetadataLegalAge, uri, cartContainsOtcProducts);
    };

export const registeredSaga = (storeName, formId) => {
    const reduxRegArtifacts = {
        storeName,
        actions: getRegActions(storeName),
        selectors: getRegSelectors(storeName),
        types: getRegTypes(storeName),
    };

    const reduxArtifacts = {
        types: getTypes(storeName),
        selectors: getSelectors(storeName),
        actions: createActions(storeName),
    };

    return [
        ...dynaForms.createSagaFor(
            reduxRegArtifacts,
            dynaFormsApi.getFormMetadata(formId || storeName),
            registeredSubmit,
            afterStepValidation,
            true,
            customDtoBuilder,
            beforeStepValidation,
            undefined,
            validateEmailAlreadyExist,
            beforeStepIncrement,
        ),
        handleCardNumberInitialization(reduxArtifacts),
        takeEvery(
            reduxRegArtifacts.types.DATA_FIELD_VALIDATION_DONE,
            setDataField(reduxRegArtifacts),
        ),
        initAddressSuggestionWatchers(reduxArtifacts),
        takeEvery(socialTypes.SET_SOCIAL_INFORMATION, setSocialInfo(reduxArtifacts)),
        takeEvery(
            reduxRegArtifacts.types.SET_DATA_FIELD_VALUE,
            radioButtonChanges(reduxRegArtifacts),
        ),
        takeEvery(reduxRegArtifacts.types.SET_DATA_FIELD_VALUE, setDataFieldValue),
        takeEvery(
            reduxRegArtifacts.types.AFTER_INITIALIZE,
            afterCheckoutRegistrationInitialize(reduxRegArtifacts),
        ),
        takeEvery(reduxRegArtifacts.types.SET_STEP, setStep(reduxRegArtifacts)),
        takeEvery(reduxRegArtifacts.types.SUBMIT, stepSubmit(reduxRegArtifacts)),
        takeEvery(
            reduxRegArtifacts.types.SUBMIT_WITHOUT_PHONE_VERIFICATION,
            doSubmitWithoutPhoneVerification(reduxRegArtifacts),
        ),
        takeEvery(reduxRegArtifacts.types.SET_STEP_BACK, stepBack(reduxRegArtifacts)),
    ];
};

export const changeBillingAddress = (storeName, formId) => {
    const reduxRegArtifacts = {
        actions: getRegActions(storeName),
        selectors: getRegSelectors(storeName),
        types: getRegTypes(storeName),
    };

    const reduxArtifacts = {
        types: getTypes(storeName),
        selectors: getSelectors(storeName),
        actions: createActions(storeName),
    };

    return [
        ...dynaForms.createSagaFor(
            reduxRegArtifacts,
            getFormMetadataFactory(formId),
            billingSubmit,
            getConfirmationFromModal,
            true,
        ),
        watchFormInitialize(reduxArtifacts, storeName),
        initAddressSuggestionWatchers(reduxArtifacts),
    ];
};

export const changeDeliveryAddress = (storeName, formId) => {
    const reduxRegArtifacts = {
        actions: getRegActions(storeName),
        selectors: getRegSelectors(storeName),
        types: getRegTypes(storeName),
    };

    const reduxArtifacts = {
        types: getTypes(storeName),
        selectors: getSelectors(storeName),
        actions: createActions(storeName),
    };

    return [
        ...dynaForms.createSagaFor(
            reduxRegArtifacts,
            getFormMetadataFactory(formId),
            deliverySubmit,
            getConfirmationFromModal,
            true,
        ),
        watchFormInitialize(reduxArtifacts, storeName),
        initAddressSuggestionWatchers(reduxArtifacts),
    ];
};
